// Push Notification Service for SFarm Monitor
// Safely handles browsers that don't support Firebase Messaging
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if messaging is supported
const isMessagingSupported = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (!('serviceWorker' in navigator)) return false;
    if (!('PushManager' in window)) return false;
    if (!('Notification' in window)) return false;
    return true;
  } catch (e) {
    return false;
  }
};

let messaging = null;

// Only initialize messaging if supported
const initializeMessaging = async () => {
  if (!isMessagingSupported()) {
    console.log('Push notifications not supported in this browser');
    return null;
  }

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging } = await import('firebase/messaging');

    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID
    };

    let app;
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }

    messaging = getMessaging(app);
    return messaging;
  } catch (e) {
    console.warn('Firebase messaging initialization failed:', e.message);
    return null;
  }
};

/**
 * Request notification permission and register FCM token
 * Handles permission, token registration, and refresh (C)
 */
export const requestNotificationPermission = async (authToken) => {
  if (!isMessagingSupported()) {
    console.log('[Push] Notifications not supported in this browser');
    return null;
  }

  try {
    // Step 1: Request permission
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);
    
    if (permission !== 'granted') {
      console.log('[Push] Notification permission denied');
      return { error: 'permission_denied' };
    }

    // Step 2: Initialize Firebase messaging
    const msg = await initializeMessaging();
    if (!msg) {
      console.log('[Push] Failed to initialize messaging');
      return { error: 'init_failed' };
    }

    // Step 3: Register service worker
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('[Push] Service Worker registered:', registration.scope);
    } catch (swError) {
      console.error('[Push] Service Worker registration failed:', swError);
      return { error: 'sw_failed' };
    }

    // Step 4: Get FCM token
    const { getToken } = await import('firebase/messaging');
    const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

    const fcmToken = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (fcmToken) {
      console.log('[Push] FCM Token obtained:', fcmToken.substring(0, 20) + '...');
      
      // Step 5: Register token with backend (handles deduplication on server)
      try {
        const response = await axios.post(`${API}/notifications/register-token`, {
          token: fcmToken,
          device_type: detectDeviceType()
        }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        console.log('[Push] Token registered with backend:', response.data);
        
        // Store token locally to detect changes
        localStorage.setItem('fcm_token', fcmToken);
        
        return fcmToken;
      } catch (apiError) {
        console.error('[Push] Failed to register token with backend:', apiError);
        return { error: 'backend_failed', token: fcmToken };
      }
    } else {
      console.log('[Push] No FCM token returned');
      return { error: 'no_token' };
    }
  } catch (error) {
    console.error('[Push] Error getting notification permission:', error);
    return { error: error.message };
  }
};

/**
 * Detect device type for token registration
 */
const detectDeviceType = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'web';
};

/**
 * Setup token refresh handler (C - onTokenRefresh)
 */
export const setupTokenRefresh = async (authToken) => {
  if (!isMessagingSupported()) return;

  try {
    const msg = await initializeMessaging();
    if (!msg) return;

    const { onMessage } = await import('firebase/messaging');
    
    // Check for token changes periodically
    setInterval(async () => {
      try {
        const { getToken } = await import('firebase/messaging');
        const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';
        const registration = await navigator.serviceWorker.ready;
        
        const newToken = await getToken(msg, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration
        });
        
        const storedToken = localStorage.getItem('fcm_token');
        
        if (newToken && newToken !== storedToken) {
          console.log('[Push] Token refreshed, updating backend...');
          
          await axios.post(`${API}/notifications/register-token`, {
            token: newToken,
            device_type: detectDeviceType()
          }, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          localStorage.setItem('fcm_token', newToken);
          console.log('[Push] Token refresh registered');
        }
      } catch (refreshError) {
        console.log('[Push] Token refresh check failed:', refreshError.message);
      }
    }, 60000 * 30); // Check every 30 minutes
    
  } catch (error) {
    console.error('[Push] Error setting up token refresh:', error);
  }
};

/**
 * Setup foreground message handler
 */
export const setupMessageListener = async (onMessageCallback) => {
  if (!isMessagingSupported()) return;

  try {
    const msg = await initializeMessaging();
    if (!msg) return;

    const { onMessage } = await import('firebase/messaging');
    
    onMessage(msg, (payload) => {
      console.log('Foreground message received:', payload);
      
      if (Notification.permission === 'granted') {
        const { title, body } = payload.notification || {};
        new Notification(title || 'SFarm Monitor', {
          body: body || 'You have a new notification',
          icon: '/icons/icon-192x192.png'
        });
      }

      if (onMessageCallback) {
        onMessageCallback(payload);
      }
    });
  } catch (error) {
    console.error('Error setting up message listener:', error);
  }
};

/**
 * Unregister FCM token
 */
export const unregisterToken = async (token, authToken) => {
  if (!token) return;

  try {
    await axios.delete(`${API}/notifications/unregister-token`, {
      data: { token },
      headers: { Authorization: `Bearer ${authToken}` }
    });
  } catch (error) {
    console.error('Error unregistering token:', error);
  }
};

/**
 * Get notification settings
 */
export const getNotificationSettings = async (authToken) => {
  try {
    const response = await axios.get(`${API}/notifications/settings`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting notification settings:', error);
    return null;
  }
};

/**
 * Update notification settings
 */
export const updateNotificationSettings = async (settings, authToken) => {
  try {
    await axios.put(`${API}/notifications/settings`, settings, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    return true;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return false;
  }
};

/**
 * Send test notification
 */
export const sendTestNotification = async (authToken) => {
  try {
    const response = await axios.post(`${API}/notifications/test`, {}, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending test notification:', error);
    return null;
  }
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = () => isMessagingSupported();

export default messaging;
